import type { ReactNode, SVGProps } from "react";

/*
 * Wealthoak icon set: 24×24 outline glyphs (round caps/joins, 1.6 stroke),
 * copied from the client app's src/ui/icons.tsx, only the ones this panel
 * uses. Glyphs marked "new" are drawn here in the same style.
 */
export type IconProps = Omit<SVGProps<SVGSVGElement>, "ref"> & {
  size?: number | string;
  strokeWidth?: number | string;
};

export type Icon = ((props: IconProps) => ReactNode) & { displayName?: string };

function make(name: string, children: ReactNode): Icon {
  const Glyph = ({ size = 18, strokeWidth = 1.6, ...rest }: IconProps) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
  Glyph.displayName = name;
  return Glyph;
}

/* ---- from the client app ---- */
export const LayoutDashboard = make("LayoutDashboard", <><path d="M3 11l9-7 9 7" /><path d="M5 10v10h14V10" /><path d="M10 20v-6h4v6" /></>);
export const Users = make("Users", <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18 14a6.5 6.5 0 0 1 3.5 6" /></>);
export const Inbox = make("Inbox", <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></>);
export const Menu = make("Menu", <path d="M4 7h16M4 12h16M4 17h16" />);
export const ChevronDown = make("ChevronDown", <path d="M6 9l6 6 6-6" />);
export const Plus = make("Plus", <path d="M12 5v14M5 12h14" />);
export const X = make("X", <path d="M6 6l12 12M18 6L6 18" />);
export const Settings = make("Settings", <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" /></>);
export const LogOut = make("LogOut", <><path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" /><path d="M15 16l4-4-4-4M19 12H9" /></>);
export const ArrowRight = make("ArrowRight", <path d="M5 12h14M13 6l6 6-6 6" />);
export const Check = make("Check", <path d="M5 12.5l4.5 4.5L19 7.5" />);
export const Info = make("Info", <><circle cx="12" cy="12" r="9" /><path d="M12 11v5.5M12 7.8v.1" /></>);
export const CalendarRange = make("CalendarRange", <><rect x="3" y="5" width="18" height="16" rx="2.5" /><path d="M3 10h18M8 3v4M16 3v4M7 14.5h3.5M13.5 14.5H17" /></>);
export const Landmark = make("Landmark", <><path d="M3.5 9.5L12 4.5l8.5 5" /><path d="M6 10v7.5M10 10v7.5M14 10v7.5M18 10v7.5" /><path d="M3.5 20.5h17" /></>);
export const AlertTriangle = make("AlertTriangle", <><path d="M12 3.5l9.5 16.5h-19z" /><path d="M12 10v4.5M12 17.4v.1" /></>);
export const Wallet = make("Wallet", <><path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H17a2 2 0 0 1 2 2v1" /><path d="M3 7.5v9A2.5 2.5 0 0 0 5.5 19H19a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H5.5A2.5 2.5 0 0 1 3 7.5z" /><path d="M16.5 14v.1" /></>);
export const Receipt = make("Receipt", <><path d="M5 3.5h14v17l-2.3-1.4-2.4 1.4-2.3-1.4-2.4 1.4L7.3 19 5 20.5z" /><path d="M8.5 8h7M8.5 12h7M8.5 16h4" /></>);
export const RefreshCw = make("RefreshCw", <><path d="M20.5 11a8.5 8.5 0 0 0-14.8-4.3L3.5 9" /><path d="M3.5 4.5V9H8" /><path d="M3.5 13a8.5 8.5 0 0 0 14.8 4.3l2.2-2.3" /><path d="M20.5 19.5V15H16" /></>);
export const Lock = make("Lock", <><rect x="4.5" y="10.5" width="15" height="10" rx="2.2" /><path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7" /><path d="M12 14.6v2.2" /></>);
export const BarChart = make("BarChart", <><path d="M3.5 20.5h17" /><path d="M6.5 20.5v-5.5M11 20.5v-10.5M15.5 20.5v-7.5M20 20.5v-3.5" /></>);

/* ---- new, drawn to match ---- */
export const UserPlus = make("UserPlus", <><circle cx="9.5" cy="8" r="3.5" /><path d="M3 20a6.5 6.5 0 0 1 13 0" /><path d="M19 8v6M16 11h6" /></>);
export const Copy = make("Copy", <><rect x="8.5" y="8.5" width="12" height="12" rx="2" /><path d="M15.5 8.5V5.5a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h3" /></>);
export const Clock = make("Clock", <><circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 2" /></>);
export const ArrowLeftRight = make("ArrowLeftRight", <><path d="M4 8h15.5M16 4.5L19.5 8 16 11.5" /><path d="M20 16H4.5M8 12.5L4.5 16 8 19.5" /></>);
export const Download = make("Download", <><path d="M12 4v11M7.5 10.5L12 15l4.5-4.5" /><path d="M4.5 19.5h15" /></>);
